import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supportApi, type TicketListQuery } from "@/api/support.api";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { SocketEvents } from "@/sockets/socket";
import { queryKeys } from "@/utils/queryKeys";
import type { TicketPriority, TicketStatus } from "@/types/domain";

export function useTicketList(query: TicketListQuery) {
  return useQuery({
    queryKey: queryKeys.support.list(query),
    queryFn: () => supportApi.list(query),
    // Safety net behind the socket events below.
    refetchInterval: 60_000,
  });
}

/** Per-topic ticket counts for the category selector. Refreshed with the rest of ["support"]. */
export function useSupportSummary() {
  return useQuery({
    queryKey: queryKeys.support.summary(),
    queryFn: () => supportApi.summary(),
    refetchInterval: 60_000,
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: queryKeys.support.detail(id ?? ""),
    queryFn: () => supportApi.getById(id as string),
    enabled: !!id,
  });
}

/**
 * Keeps the inbox and the open thread current: a customer's new message
 * arrives as `admin:support:message`; a newly raised ticket / SOS as
 * `admin:ride:update` (type SOS | REPORT).
 */
export function useSupportRealtimeSync() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["support"] });
  useSocketEvent(SocketEvents.AdminSupportMessage, refresh);
  useSocketEvent(SocketEvents.AdminRideUpdate, refresh);
}

function useInvalidateSupport() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["support"] });
}

export function useReplyToTicket() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => supportApi.reply(id, text),
    onSuccess: invalidate,
  });
}

export function useUpdateTicket() {
  const invalidate = useInvalidateSupport();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; status?: TicketStatus; priority?: TicketPriority; resolution?: string }) =>
      supportApi.update(id, payload),
    onSuccess: invalidate,
  });
}
